// UNA LUCE NUOVA NASCE DOVE SI VEDE (Elena, 2026-10-14). Il motore la fa nascere nell'origine con i
// valori di Babylon, e le puntiformi e i faretti calano col quadrato della distanza: su un modello di
// qualche metro bastano, su uno in millimetri — una sedia alta 1300 unità — la luce nasceva dentro la
// base, e anche spostata sopra arrivava a un millesimo. Sembrava rotta. Nasce invece sopra il modello,
// rivolta verso di lui, con un'intensità che arriva: poi la si regola a mano.
//
// SOLO ALLA NASCITA, e solo per puntiformi e faretti: direzionali ed emisferiche non calano con la
// distanza. Le luci salvate non cambiano.

// L'illuminamento che si vuole sul modello: quanto dà, a occhio, l'emisferica di default.
const TARGET_ILLUMINANCE = 2.5;

type SceneFrame = { center: any; size: number; top: number; radius: number };

const isPositional = (lightType: string) => lightType === 'point_light' || lightType === 'spot_light';

/** Il riquadro di ciò che si illumina: le mesh vere della scena, senza pavimento, gizmo e aiuti. */
const sceneFrame = (scene: any): SceneFrame | null => {
    const meshes: any[] = (scene?.meshes ?? []).filter(
        (mesh: any) =>
            mesh.name !== 'ground' &&
            mesh.metadata?.isRealMesh !== false &&
            !mesh.metadata?.isEditControl &&
            mesh.isEnabled?.() &&
            (mesh.getTotalVertices?.() ?? 0) > 0
    );
    if (!meshes.length) return null;

    let min: any = null;
    let max: any = null;
    meshes.forEach((mesh) => {
        mesh.computeWorldMatrix(true);
        const box = mesh.getBoundingInfo().boundingBox;
        min = min ? BABYLON.Vector3.Minimize(min, box.minimumWorld) : box.minimumWorld.clone();
        max = max ? BABYLON.Vector3.Maximize(max, box.maximumWorld) : box.maximumWorld.clone();
    });

    const extent = max.subtract(min);
    const size = extent.length();
    if (!size || !Number.isFinite(size)) return null;

    return { center: BABYLON.Vector3.Center(min, max), size, top: max.y, radius: Math.max(extent.x, extent.z) / 2 };
};

/** L'intensità che a quella distanza dà sul modello l'illuminamento voluto. */
const reachingIntensity = (distance: number) => TARGET_ILLUMINANCE * distance * distance;

/** Dove sta e dove guarda una luce messa bene: il faretto sopra il modello, la puntiforme in alto di lato. */
const pose = (frame: SceneFrame, lightType: string) => {
    const { center, size, top } = frame;
    const position =
        lightType === 'spot_light'
            ? new BABYLON.Vector3(center.x, top + size * 0.5, center.z)
            : new BABYLON.Vector3(center.x + size * 0.5, top + size * 0.25, center.z + size * 0.5);
    return { position, direction: center.subtract(position).normalize(), distance: BABYLON.Vector3.Distance(position, center) };
};

/**
 * IL POSTO A RIPOSO, quello di «Reset transform» (Elena, 2026-10-14): lo stesso di una luce nuova,
 * senza toccare intensità e cono, che sono scelte di chi la regola. Null se la luce non ha un posto
 * (direzionali, emisferiche) o se la scena è vuota: chi chiede tiene l'origine.
 */
export const restingPose = (light: any, lightType: string): { position: [number, number, number]; direction: [number, number, number] } | null => {
    if (!light || !isPositional(lightType)) return null;
    const frame = sceneFrame(light.getScene?.());
    if (!frame) return null;

    const { position, direction } = pose(frame, lightType);
    return { position: [position.x, position.y, position.z], direction: [direction.x, direction.y, direction.z] };
};

/**
 * Mette la luce appena nata dove illumina il modello: il faretto sopra, rivolto al centro e aperto
 * quanto basta a coprirlo; la puntiforme in alto, davanti e di lato. Su una scena vuota non fa niente.
 */
export const placeNewLight = (light: any, lightType: string) => {
    if (!light || !isPositional(lightType)) return;
    const frame = sceneFrame(light.getScene?.());
    if (!frame) return;

    const { radius } = frame;
    const { position, direction, distance } = pose(frame, lightType);

    light.position.copyFrom(position);
    light.intensity = reachingIntensity(distance);

    if (lightType === 'spot_light') {
        light.direction.copyFrom(direction);
        // Il cono intero copre il modello con un po' di margine; mai uno spillo, mai un emisfero.
        const angle = 2 * Math.atan((radius * 1.3) / distance);
        light.angle = Math.min(Math.max(angle, (20 * Math.PI) / 180), (90 * Math.PI) / 180);
    }
};

/**
 * Fin dove arriva il cursore dell'intensità. Per puntiformi e faretti dipende dalla scena: con il
 * massimo fisso a 10 un modello in millimetri non si illuminava da nessuna posizione del cursore.
 */
export const intensityCeiling = (light: any, lightType: string): number => {
    const frame = isPositional(lightType) ? sceneFrame(light?.getScene?.()) : null;
    return frame ? Math.max(10, Math.ceil(reachingIntensity(frame.size) * 4)) : 10;
};
